(() => {
  const STORAGE_KEY = 'fohProjectList.v1';
  const PROJECT_PAGE_URL = '/pages/project-list';

  const selectors = {
    button: '[data-foh-project-button]',
    buttonText: '[data-foh-project-button-text]',
    page: '[data-foh-project-list-page]',
    grid: '[data-foh-project-list-grid]',
    empty: '[data-foh-project-list-empty]',
    status: '[data-foh-project-list-status]',
    addAll: '[data-foh-project-list-add-all]',
    clear: '[data-foh-project-list-clear]',
    copy: '[data-foh-project-list-copy]'
  };

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('foh-project-list:updated', { detail: { items } }));
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return url;
    }
  };

  const getProductFormVariantId = (button) => {
    const section = button.closest('.product, .product__info-wrapper, section') || document;
    const input = section.querySelector('product-form form [name="id"], form[action*="/cart/add"] [name="id"]');
    return input && input.value ? input.value : button.dataset.variantId;
  };

  const buttonItem = (button) => ({
    handle: button.dataset.productHandle,
    id: button.dataset.productId,
    title: button.dataset.productTitle,
    url: normalizeUrl(button.dataset.productUrl),
    image: button.dataset.productImage,
    variantId: getProductFormVariantId(button),
    price: Number(button.dataset.productPrice || 0),
    available: button.dataset.productAvailable === 'true',
    savedAt: new Date().toISOString()
  });

  const setButtonState = (button, isSaved) => {
    const text = button.querySelector(selectors.buttonText);
    const saveLabel = button.dataset.saveLabel || 'Add to Project List';
    const savedLabel = button.dataset.savedLabel || 'Added to Project List';
    const title = button.dataset.productTitle || 'this product';

    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    button.setAttribute('aria-label', `${isSaved ? savedLabel : saveLabel}: ${title}`);
    if (text) text.textContent = isSaved ? savedLabel : saveLabel;
  };

  const refreshButtons = () => {
    const items = read();
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll(selectors.button).forEach((button) => {
      setButtonState(button, handles.has(button.dataset.productHandle));
    });

  };

  const toggle = (button) => {
    const item = buttonItem(button);
    if (!item.handle) return;

    const items = read();
    const exists = items.some((saved) => saved.handle === item.handle);
    const nextItems = exists
      ? items.filter((saved) => saved.handle !== item.handle)
      : [item, ...items.filter((saved) => saved.handle !== item.handle)];

    write(nextItems.slice(0, 100));
  };

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[char]));

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    return amount > 0
      ? amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      : '';
  };

  const fetchProduct = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Could not load ${handle}`);
    return response.json();
  };

  const pickVariant = (product, savedVariantId) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const saved = variants.find((variant) => String(variant.id) === String(savedVariantId) && variant.available);
    if (saved) return saved;
    return variants.find((variant) => variant.available) || variants[0];
  };

  const imageFor = (product, saved) => {
    if (product.featured_image) return product.featured_image;
    if (Array.isArray(product.images) && product.images.length) return product.images[0];
    return saved.image || '';
  };

  const cardMarkup = (product, saved) => {
    const variant = pickVariant(product, saved.variantId);
    const available = Boolean(variant && variant.available);
    const image = imageFor(product, saved);
    const price = variant && variant.price ? money(variant.price) : money(product.price || saved.price);
    const url = product.url || saved.url || `/products/${saved.handle}`;
    const safeTitle = escapeHtml(product.title || saved.title);
    const safeVariantTitle = escapeHtml(variant ? variant.title : '');
    const safeUrl = escapeHtml(url);
    const safeImage = escapeHtml(image);
    const safeHandle = escapeHtml(saved.handle);
    const variantText = variant && product.variants && product.variants.length > 1 ? `<p class="foh-project-list-card__meta">Saved option: ${safeVariantTitle}</p>` : '';

    return `
      <article class="foh-project-list-card" data-foh-project-card data-handle="${safeHandle}">
        <a class="foh-project-list-card__image" href="${safeUrl}">
          ${safeImage ? `<img src="${safeImage}" alt="${safeTitle}">` : ''}
        </a>
        <div class="foh-project-list-card__body">
          <a class="foh-project-list-card__title" href="${safeUrl}">${safeTitle}</a>
          ${price ? `<p class="foh-project-list-card__meta">${price}</p>` : ''}
          ${variantText}
          <p class="foh-project-list-card__meta">${available ? 'Available' : 'Currently unavailable'}</p>
          <div class="foh-project-list-card__buttons">
            <button type="button" class="button button--secondary" data-foh-project-add-one data-variant-id="${variant ? variant.id : ''}" ${available ? '' : 'disabled'}>Add to Cart</button>
            <button type="button" class="button button--tertiary" data-foh-project-remove data-handle="${saved.handle}">Remove</button>
          </div>
        </div>
      </article>`;
  };

  const setStatus = (page, message) => {
    const status = page.querySelector(selectors.status);
    if (status) status.textContent = message || '';
  };

  const loadSharedItems = () => {
    const params = new URLSearchParams(window.location.search);
    const handles = (params.get('items') || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    if (!handles.length) return;

    const current = read();
    const known = new Set(current.map((item) => item.handle));
    const imported = handles
      .filter((handle) => !known.has(handle))
      .map((handle) => ({ handle, title: handle, url: `/products/${handle}`, image: '', variantId: '', price: 0, available: true, savedAt: new Date().toISOString() }));

    if (imported.length) write([...imported, ...current].slice(0, 100));
  };

  const renderPage = async () => {
    const page = document.querySelector(selectors.page);
    if (!page) return;

    loadSharedItems();

    const grid = page.querySelector(selectors.grid);
    const empty = page.querySelector(selectors.empty);
    const items = read();

    if (!grid) return;

    if (!items.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      setStatus(page, '');
      return;
    }

    if (empty) empty.hidden = true;
    setStatus(page, 'Loading your saved products...');

    const loaded = await Promise.all(
      items.map(async (saved) => {
        try {
          const product = await fetchProduct(saved.handle);
          return { saved, product };
        } catch (error) {
          return { saved, product: null };
        }
      })
    );

    const validHandles = new Set(loaded.filter((item) => item.product).map((item) => item.saved.handle));
    const validItems = items.filter((item) => validHandles.has(item.handle));
    if (validItems.length !== items.length) write(validItems);

    grid.innerHTML = loaded
      .filter((item) => item.product)
      .map(({ saved, product }) => cardMarkup(product, saved))
      .join('');

    setStatus(page, `${validItems.length} saved ${validItems.length === 1 ? 'item' : 'items'} on this device.`);
  };

  const addToCart = async (items) => {
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!response.ok) throw new Error('Cart add failed');
    return response.json();
  };

  const addAllAvailable = async (page) => {
    const buttons = Array.from(page.querySelectorAll('[data-foh-project-add-one]:not(:disabled)'));
    const items = buttons
      .map((button) => ({ id: Number(button.dataset.variantId), quantity: 1 }))
      .filter((item) => item.id);

    if (!items.length) {
      setStatus(page, 'No available saved products to add right now.');
      return;
    }

    setStatus(page, 'Adding saved products to cart...');
    await addToCart(items);
    setStatus(page, 'Saved products added to cart.');
    window.location.href = window.routes?.cart_url || '/cart';
  };

  const copyShareLink = async (page) => {
    const handles = read().map((item) => item.handle).filter(Boolean);
    if (!handles.length) {
      setStatus(page, 'Your Project List is empty.');
      return;
    }

    const url = `${window.location.origin}${PROJECT_PAGE_URL}?items=${encodeURIComponent(handles.join(','))}`;
    try {
      await navigator.clipboard.writeText(url);
      setStatus(page, 'Project List link copied. Links work best for shorter lists.');
    } catch (error) {
      setStatus(page, url);
    }
  };

  document.addEventListener('click', async (event) => {
    const saveButton = event.target.closest(selectors.button);
    if (saveButton) {
      event.preventDefault();
      toggle(saveButton);
      return;
    }

    const page = event.target.closest(selectors.page);
    if (!page) return;

    const removeButton = event.target.closest('[data-foh-project-remove]');
    if (removeButton) {
      const handle = removeButton.dataset.handle;
      write(read().filter((item) => item.handle !== handle));
      await renderPage();
      return;
    }

    const addOne = event.target.closest('[data-foh-project-add-one]');
    if (addOne && addOne.dataset.variantId) {
      setStatus(page, 'Adding product to cart...');
      try {
        await addToCart([{ id: Number(addOne.dataset.variantId), quantity: 1 }]);
        setStatus(page, 'Product added to cart.');
      } catch (error) {
        setStatus(page, 'Sorry, that product could not be added to cart.');
      }
      return;
    }

    if (event.target.closest(selectors.addAll)) {
      try {
        await addAllAvailable(page);
      } catch (error) {
        setStatus(page, 'Sorry, the saved products could not be added to cart.');
      }
      return;
    }

    if (event.target.closest(selectors.clear)) {
      if (window.confirm('Clear your Project List on this device?')) {
        write([]);
        await renderPage();
      }
      return;
    }

    if (event.target.closest(selectors.copy)) {
      await copyShareLink(page);
    }
  });

  window.addEventListener('foh-project-list:updated', () => {
    refreshButtons();
    renderPage();
  });

  document.addEventListener('DOMContentLoaded', () => {
    refreshButtons();
    renderPage();
  });
})();
